import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { AdFormValues } from 'organisms/AdForm/AdForm';
import CampaignInfoStep from 'organisms/CampaignSteps/CampaignInfoStep';
import CampaignCheckoutStep from 'organisms/CampaignSteps/CheckoutStep';
import { DOMAIN_BASE_SHORT, ROUTES } from 'config/Nav';
import useStyles from './styles';
import type { CampaignCheckoutValues } from 'types/campaignCheckout';
import { FormikHelpers } from 'formik';
import withProgress from 'hocs/withProgress';
import { WithProgressTypes } from 'hocs/withProgress/withProgress';
import { reportError } from 'lib/errors';
import { useSnackbar } from 'notistack';
import { useMutation } from '@apollo/client/react';
import { SUBMIT_CAMPAIGN } from 'lib/graphql/mutations/campaigns';
import { SubmitCampaignInput, SubmitCampaignResponse } from 'types/campaign';
import { useRouter } from 'next/router';
import { PageProps } from 'types/page';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Seo, useAuth, PaymentType } from '@elevator/ui';
import { useRegisterFullWidthSlot } from 'hocs/withLayout';
import {
  clearCampaignDraft,
  loadCampaignDraft,
  saveCampaignDraft,
} from 'lib/campaignDraft';

const STEP_LABELS = ['Campaign Info', 'Checkout'] as const;

const createInitialCampaignState = (): AdFormValues =>
  ({
    campaignName: '',
    campaignType: null,
    startDate: 'default',
    designProvided: true,
    files: [],
    clickThroughUrl: '',
  }) as AdFormValues;

const Campaign = (props: CampaignPageProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, hydrateUser } = useAuth();
  const router = useRouter();
  const stripe = useStripe();
  const elements = useElements();

  const [activeStep, setActiveStep] = React.useState(0);
  const [campaign, setCampaign] = React.useState(createInitialCampaignState);
  const [totalCost, setTotalCost] = React.useState(0);

  const resetCampaignFlow = React.useCallback(() => {
    setActiveStep(0);
    setCampaign(createInitialCampaignState());
    setTotalCost(0);
  }, []);

  React.useEffect(() => {
    if (!router.isReady) return;

    void (async () => {
      const draft = await loadCampaignDraft();
      if (!draft) return;

      const resumeFromAuth = router.query.resume === '1';
      if (!resumeFromAuth && draft.activeStep === 0) return;

      setCampaign(draft.campaign);
      setTotalCost(draft.totalCost);
      setActiveStep(draft.activeStep);

      if (resumeFromAuth) {
        await hydrateUser(false);
        const { resume, ...restQuery } = router.query;
        void router.replace({ pathname: router.pathname, query: restQuery }, undefined, {
          shallow: true,
        });
      }
    })();
  }, [router.isReady, router.query.resume, hydrateUser, router]);

  // Browser back/forward (bfcache) can restore in-memory step state after a completed submission.
  React.useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;

      void (async () => {
        const draft = await loadCampaignDraft();
        if (!draft) {
          resetCampaignFlow();
        }
      })();
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [resetCampaignFlow]);

  const persistCampaignDraft = React.useCallback(async () => {
    await saveCampaignDraft({ activeStep, totalCost, campaign });
  }, [activeStep, totalCost, campaign]);

  const [submitCampaign] = useMutation<
    SubmitCampaignResponse,
    { input: SubmitCampaignInput }
  >(SUBMIT_CAMPAIGN);
  const { enqueueSnackbar } = useSnackbar();

  const next = () => setActiveStep((s) => s + 1);
  const prev = () => setActiveStep((s) => s - 1);

  const handleSubmitCampaignInfo = (campaignInfo: AdFormValues) => {
    setCampaign(campaignInfo);
    void saveCampaignDraft({
      activeStep: 1,
      totalCost,
      campaign: campaignInfo,
    });
    next();
  };

  const handleSubmitPayment = async (
    values: CampaignCheckoutValues,
    actions: FormikHelpers<CampaignCheckoutValues>,
    paymentType: PaymentType,
    paypalOrderId: string = null,
    bitPayInvoiceId: string = null,
  ) => {
    const { startProcessing, stopProcessing, setProcessingText } = props;
    startProcessing();

    if (!currentUser) {
      actions.setSubmitting(false);
      enqueueSnackbar('You must be signed in to submit a campaign.', {
        variant: 'error',
        preventDuplicate: true,
      });
      stopProcessing();
      return;
    }

    const { campaignType, campaignName, ...campaignInfo } = campaign;
    const input: SubmitCampaignInput = {
      paymentType,
      paymentMethod: {},
      campaign: { ...campaignInfo, name: campaignName, typeId: campaignType },
      total: totalCost,
    };

    if (paymentType === 'PayPal') {
      setProcessingText('Retrieving PayPal order information...');
      if (!paypalOrderId) {
        actions.setSubmitting(false);
        enqueueSnackbar('PayPal order information is missing. Please try again.', {
          variant: 'error',
          preventDuplicate: true,
        });
        stopProcessing();
        return;
      }
      input.paymentMethod.paypalOrderId = paypalOrderId;
    } else if (paymentType === 'BitPay') {
      setProcessingText('Retrieving BitPay order information...');
      input.paymentMethod.bitPayInvoiceId = bitPayInvoiceId;
    } else {
      setProcessingText('Validating card information...');
      try {
        if (!elements || !stripe) throw new Error('Payment form is not ready');

        const cardState = values.cardInfo as {
          complete?: boolean;
          error?: { message?: string };
        };
        if (!cardState?.complete || cardState?.error) {
          throw new Error(cardState?.error?.message || 'Please enter a valid card');
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) throw new Error('Card field is not ready');

        const { token, error } = await stripe.createToken(cardElement, {
          name: values.nameOnCard,
          address_zip: values.postalCode,
          address_country: values.country,
          currency: 'usd',
        });
        if (error) throw new Error(error.message);
        input.paymentMethod.stripeCardTokenId = token.id;
      } catch (err) {
        actions.setSubmitting(false);
        reportError(err, {
          metaData: { operation: 'stripe createToken' },
          severity: 'error',
        });
        enqueueSnackbar('Invalid card information', { variant: 'error' });
        stopProcessing();
        return;
      }
    }

    try {
      setProcessingText('Submitting campaign information and assets...');
      const { data } = await submitCampaign({ variables: { input } });
      if (!data?.submitCampaign?.orderNumber) {
        throw new Error('Campaign submission failed: No order number returned');
      }
      clearCampaignDraft();
      resetCampaignFlow();
      router.push({
        pathname: ROUTES.ADVERTISE.CONFIRMATION.to,
        query: { campaignId: data.submitCampaign.orderNumber },
      });
    } catch (err) {
      actions.setSubmitting(false);
      reportError(err, {
        metaData: {
          operation: 'mutation submitCampaign',
          input: { paymentType, campaignName: campaign.campaignName, totalCost },
        },
        severity: 'error',
      });
      let errorMessage = 'Failed to submit campaign. Please try again.';
      if (err instanceof Error) {
        if (err.message.includes('validation') || err.message.includes('Validation')) {
          errorMessage =
            'Campaign validation failed. Please check your campaign details.';
        } else if (err.message.includes('file') || err.message.includes('upload')) {
          errorMessage = 'File upload failed. Please check your files and try again.';
        } else if (err.message.includes('payment') || err.message.includes('Payment')) {
          errorMessage =
            'Payment processing failed. Please check your payment information.';
        }
      }
      enqueueSnackbar(errorMessage, { variant: 'error', preventDuplicate: true });
      stopProcessing();
    }
  };

  const renderStep = (index: number) => {
    switch (index) {
      case 0:
        return (
          <CampaignInfoStep
            campaign={campaign}
            handleSubmit={handleSubmitCampaignInfo}
            handleBackClick={prev}
            totalCost={totalCost}
            updateTotalCost={setTotalCost}
          />
        );
      case 1:
        return (
          <CampaignCheckoutStep
            handleSubmit={handleSubmitPayment}
            handleBackClick={prev}
            totalCost={totalCost}
            currentUser={currentUser}
            typeId={campaign.campaignType != null ? String(campaign.campaignType) : ''}
            onBeforeAuthRedirect={persistCampaignDraft}
          />
        );
      default:
        return null;
    }
  };

  const fullWidthStepper = useMemo(
    () => (
      <Box className={classes.stepperWrapper}>
        <Box className={classes.stepperFullWidth}>
          <Paper elevation={1} className={classes.stepperPaper}>
            {mobile ? (
              <Box className={classes.mobileStepper}>
                <Typography>
                  <b>Step {activeStep + 1}</b> of {STEP_LABELS.length} —{' '}
                  {STEP_LABELS[activeStep]}
                </Typography>
              </Box>
            ) : (
              <Stepper activeStep={activeStep} className={classes.steps}>
                {STEP_LABELS.map((label) => (
                  <Step key={label} className={classes.step}>
                    <StepLabel
                      slotProps={{
                        stepIcon: {
                          classes: {
                            active: classes.activeIcon,
                            completed: classes.completedIcon,
                          },
                        },
                      }}
                    >
                      <Typography>{label}</Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}
          </Paper>
        </Box>
      </Box>
    ),
    [activeStep, mobile, classes],
  );

  useRegisterFullWidthSlot(fullWidthStepper);

  return (
    <Stack
      className={classes.container}
      direction="column"
      spacing={3}
      data-layout="campaign"
      component="main"
      sx={{ flex: 1, minHeight: 0, alignSelf: 'stretch' }}
    >
      <Seo
        type="website"
        data={{
          baseUrl: DOMAIN_BASE_SHORT,
          title: 'ELEVATOR Advertising Campaign',
          description: 'Submit your advertising campaign to the ELEVATOR Ad Network.',
          path: ROUTES.CAMPAIGN.to,
        }}
      />
      <Box className={classes.stepContentWrapper}>{renderStep(activeStep)}</Box>
    </Stack>
  );
};

export interface CampaignPageProps extends WithProgressTypes, PageProps {}

const CampaignWithProgress = withProgress(Campaign);

export default CampaignWithProgress;
